import { KonvaEventObject } from 'konva/lib/Node';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PIXELS_PER_METER } from '../../model/constants/mapScale';
import { WarehouseMapTool } from '../../model/types';
import { calculateBackgroundScaleMultiplier, BackgroundScaleMultiplierResult } from '../helpers/calculateBackgroundScaleMultiplier';
import { Point } from '../helpers/coordinates';
import { getRelativeCursorPosition } from '../helpers/getRelativeCursorPosition';

type Phase = 'idle' | 'waitingFirst' | 'drawing' | 'measured';

export interface BackgroundScaleMeasureApi {
  phase: Phase;
  start: Point | null;
  end: Point | null;
  measuredPixels: number | null;
  referenceMeters: number | null;
  result: BackgroundScaleMultiplierResult | null;
  startMeasure: () => void;
  clearMeasure: () => void;
  setReferenceMeters: (value: number | null) => void;
  onMouseDown: (event: KonvaEventObject<MouseEvent>) => boolean;
  onMouseMove: (event: KonvaEventObject<MouseEvent>) => boolean;
}

export const useBackgroundScaleMeasure = (activeTool: WarehouseMapTool): BackgroundScaleMeasureApi => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [start, setStart] = useState<Point | null>(null);
  const [end, setEnd] = useState<Point | null>(null);
  const [referenceMeters, setReferenceMeters] = useState<number | null>(null);

  useEffect(() => {
    if (activeTool !== WarehouseMapTool.EDIT_BACKGROUND) {
      setPhase('idle');
      setStart(null);
      setEnd(null);
    }
  }, [activeTool]);

  const measuredPixels = useMemo(() => {
    if (!start || !end) return null;
    return Math.hypot(end.x - start.x, end.y - start.y);
  }, [end, start]);

  const result = useMemo(() => {
    if (!measuredPixels || !referenceMeters) return null;
    return calculateBackgroundScaleMultiplier({ measuredPixels, referenceMeters, pixelsPerMeter: PIXELS_PER_METER });
  }, [measuredPixels, referenceMeters]);

  const startMeasure = useCallback((): void => {
    setStart(null);
    setEnd(null);
    setPhase('waitingFirst');
  }, []);

  const clearMeasure = useCallback((): void => {
    setStart(null);
    setEnd(null);
    setPhase('idle');
  }, []);

  const onMouseDown = useCallback(
    (event: KonvaEventObject<MouseEvent>): boolean => {
      if (activeTool !== WarehouseMapTool.EDIT_BACKGROUND || (phase !== 'waitingFirst' && phase !== 'drawing')) return false;
      if (event.evt.button !== 0) return true;

      const point = getRelativeCursorPosition(event.target.getStage());
      if (!point) return true;

      if (phase === 'waitingFirst') {
        setStart(point);
        setEnd(point);
        setPhase('drawing');
        return true;
      }

      setEnd(point);
      setPhase('measured');
      return true;
    },
    [activeTool, phase],
  );

  const onMouseMove = useCallback(
    (event: KonvaEventObject<MouseEvent>): boolean => {
      if (activeTool !== WarehouseMapTool.EDIT_BACKGROUND || phase !== 'drawing' || !start) return false;
      const point = getRelativeCursorPosition(event.target.getStage());
      if (point) setEnd(point);
      return true;
    },
    [activeTool, phase, start],
  );

  return {
    phase,
    start,
    end,
    measuredPixels,
    referenceMeters,
    result,
    startMeasure,
    clearMeasure,
    setReferenceMeters,
    onMouseDown,
    onMouseMove,
  };
};
